/**
 * Sector and Region Data Utilities
 *
 * Provides structure for manual sector and region input for investments.
 * Users should enter this data from their fund factsheets/documentation.
 *
 * Sectors follow GICS (Global Industry Classification Standard)
 * Regions follow standard geographic classifications
 */

// Standard sector list (GICS Classification + Cash/Other for funds holding cash)
export const SECTORS = [
  'Information Technology',
  'Financials',
  'Health Care',
  'Consumer Discretionary',
  'Industrials',
  'Communication Services',
  'Consumer Staples',
  'Energy',
  'Materials',
  'Utilities',
  'Real Estate',
  'Cash/Other'
];

// Standard region list
export const REGIONS = [
  'North America',
  'Europe',
  'United Kingdom',
  'Japan',
  'Asia Pacific',
  'Emerging Markets',
  'Middle East/Africa',
  'Latin America',
  'Other'
];

// Sector colors for visualization
export const SECTOR_COLORS = {
  'Information Technology': '#3b82f6',
  'Financials': '#10b981',
  'Health Care': '#ec4899',
  'Consumer Discretionary': '#f97316',
  'Industrials': '#8b5cf6',
  'Communication Services': '#06b6d4',
  'Consumer Staples': '#eab308',
  'Energy': '#ef4444',
  'Materials': '#84cc16',
  'Utilities': '#14b8a6',
  'Real Estate': '#f59e0b',
  'Cash/Other': '#6b7280'
};

// Region colors for visualization
export const REGION_COLORS = {
  'North America': '#3b82f6',
  'Europe': '#10b981',
  'United Kingdom': '#8b5cf6',
  'Japan': '#ef4444',
  'Asia Pacific': '#f97316',
  'Emerging Markets': '#eab308',
  'Middle East/Africa': '#06b6d4',
  'Latin America': '#84cc16',
  'Other': '#6b7280'
};

/**
 * Get sector breakdown for an investment
 * Returns only manually entered data - no static lookups
 * @param {Object} investment - Investment object with sectorBreakdown field
 * @returns {Object|null} Sector breakdown or null if not entered
 */
export const getSectorBreakdown = (investment) => {
  // Cash, Crypto, Bonds typically don't have sector allocation
  const category = (investment.category || '').toLowerCase();
  if (['cash', 'cryptocurrency', 'crypto', 'bonds', 'fixed income', 'money market'].includes(category)) {
    return null;
  }

  // Return manually entered sector data only
  if (investment.sectorBreakdown && Object.keys(investment.sectorBreakdown).length > 0) {
    return investment.sectorBreakdown;
  }

  // No sector data available - user needs to enter it manually
  return null;
};

/**
 * Get region breakdown for an investment
 * Returns only manually entered data
 * @param {Object} investment - Investment object with regionBreakdown field
 * @returns {Object|null} Region breakdown or null if not entered
 */
export const getRegionBreakdown = (investment) => {
  // Cash typically doesn't have region allocation (or is 100% local)
  const category = (investment.category || '').toLowerCase();
  if (['cash', 'money market'].includes(category)) {
    return null;
  }

  // Return manually entered region data only
  if (investment.regionBreakdown && Object.keys(investment.regionBreakdown).length > 0) {
    return investment.regionBreakdown;
  }

  // No region data available - user needs to enter it manually
  return null;
};

/**
 * Calculate portfolio-level sector allocation from investments with manual data
 * @param {Array} investments - Array of investment objects
 * @returns {Object} Portfolio sector allocation with totals
 */
export const calculatePortfolioSectorAllocation = (investments = []) => {
  const sectorTotals = {};
  const investmentSectors = [];
  let totalPortfolioValue = 0;
  let totalWithSectorData = 0;

  // Initialize all sectors to 0
  SECTORS.forEach(sector => {
    sectorTotals[sector] = 0;
  });

  investments.forEach(investment => {
    const value = investment.amount || (investment.quantity * (investment.currentPrice || investment.pricePerUnit || 0));
    if (value <= 0) return;

    totalPortfolioValue += value;
    const sectorBreakdown = getSectorBreakdown(investment);

    const investmentData = {
      name: investment.name,
      ticker: investment.ticker,
      value,
      category: investment.category,
      sectors: {},
      hasSectorData: !!sectorBreakdown
    };

    if (sectorBreakdown) {
      totalWithSectorData += value;
      // Calculate sector values for this investment
      SECTORS.forEach(sector => {
        const percentage = sectorBreakdown[sector] || 0;
        const sectorValue = (value * percentage) / 100;
        sectorTotals[sector] += sectorValue;
        if (percentage > 0) {
          investmentData.sectors[sector] = percentage;
        }
      });
    } else {
      // No sector data - mark as needing input
      investmentData.sectors = null;
    }

    investmentSectors.push(investmentData);
  });

  // Calculate percentages based on total portfolio
  const sectorPercentages = {};
  SECTORS.forEach(sector => {
    sectorPercentages[sector] = totalPortfolioValue > 0
      ? (sectorTotals[sector] / totalPortfolioValue) * 100
      : 0;
  });

  return {
    totals: sectorTotals,
    percentages: sectorPercentages,
    investments: investmentSectors,
    totalPortfolioValue,
    totalWithSectorData,
    coveragePercent: totalPortfolioValue > 0 ? (totalWithSectorData / totalPortfolioValue) * 100 : 0
  };
};

/**
 * Calculate portfolio-level region allocation from investments with manual data
 * @param {Array} investments - Array of investment objects
 * @returns {Object} Portfolio region allocation with totals
 */
export const calculatePortfolioRegionAllocation = (investments = []) => {
  const regionTotals = {};
  const investmentRegions = [];
  let totalPortfolioValue = 0;
  let totalWithRegionData = 0;

  // Initialize all regions to 0
  REGIONS.forEach(region => {
    regionTotals[region] = 0;
  });

  investments.forEach(investment => {
    const value = investment.amount || (investment.quantity * (investment.currentPrice || investment.pricePerUnit || 0));
    if (value <= 0) return;

    totalPortfolioValue += value;
    const regionBreakdown = getRegionBreakdown(investment);

    const investmentData = {
      name: investment.name,
      ticker: investment.ticker,
      value,
      category: investment.category,
      regions: {},
      hasRegionData: !!regionBreakdown
    };

    if (regionBreakdown) {
      totalWithRegionData += value;
      // Calculate region values for this investment
      REGIONS.forEach(region => {
        const percentage = regionBreakdown[region] || 0;
        const regionValue = (value * percentage) / 100;
        regionTotals[region] += regionValue;
        if (percentage > 0) {
          investmentData.regions[region] = percentage;
        }
      });
    } else {
      // No region data - mark as needing input
      investmentData.regions = null;
    }

    investmentRegions.push(investmentData);
  });

  // Calculate percentages based on total portfolio
  const regionPercentages = {};
  REGIONS.forEach(region => {
    regionPercentages[region] = totalPortfolioValue > 0
      ? (regionTotals[region] / totalPortfolioValue) * 100
      : 0;
  });

  return {
    totals: regionTotals,
    percentages: regionPercentages,
    investments: investmentRegions,
    totalPortfolioValue,
    totalWithRegionData,
    coveragePercent: totalPortfolioValue > 0 ? (totalWithRegionData / totalPortfolioValue) * 100 : 0
  };
};

/**
 * Validate sector breakdown totals
 * @param {Object} sectorBreakdown - Object with sector percentages
 * @returns {Object} Validation result { valid: boolean, total: number, message: string }
 */
export const validateSectorBreakdown = (sectorBreakdown) => {
  if (!sectorBreakdown || Object.keys(sectorBreakdown).length === 0) {
    return { valid: true, total: 0, message: 'No sector data entered' };
  }

  const total = Object.values(sectorBreakdown).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  // Allow totals up to 100% - less is OK (funds may hold cash/other), but more than 100.5% is an error
  if (total > 100.5) {
    return {
      valid: false,
      total,
      message: `Sector percentages cannot exceed 100% (currently ${total.toFixed(1)}%)`
    };
  }

  return { valid: true, total, message: 'Valid' };
};

/**
 * Validate region breakdown totals
 * @param {Object} regionBreakdown - Object with region percentages
 * @returns {Object} Validation result { valid: boolean, total: number, message: string }
 */
export const validateRegionBreakdown = (regionBreakdown) => {
  if (!regionBreakdown || Object.keys(regionBreakdown).length === 0) {
    return { valid: true, total: 0, message: 'No region data entered' };
  }

  const total = Object.values(regionBreakdown).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);

  // Allow totals up to 100% - less is OK (funds may hold cash/other), but more than 100.5% is an error
  if (total > 100.5) {
    return {
      valid: false,
      total,
      message: `Region percentages cannot exceed 100% (currently ${total.toFixed(1)}%)`
    };
  }

  return { valid: true, total, message: 'Valid' };
};
